import classNames from 'classnames';
import { isEmpty, isUndefined, noop, omit } from 'lodash-es';
import { CaretDoubleDown, CaretDoubleUp, X } from 'phosphor-react';
import type { PropsWithChildren } from 'react';
import { Children, createContext, useContext, useEffect, useId, useState } from 'react';

import type { Sorting } from '../../table';
import { Badge } from '../badge/badge';
import { ButtonActionQuietSurrogate, ButtonCtaGhost } from '../button/button';
import type { Option } from '../form';
import { Searchfield } from './searchfield';
import { SortingController } from './sorting-controller';

const suchbegriff = 'suchbegriff';

type ExpandArea = 'filter' | 'sort' | null;

type FreitextSuche = {
  [suchbegriff]?: string;
};

type FilteringContextConfiguration<F extends FreitextSuche = FreitextSuche> = {
  filterValues: F;
  onSingleFilterChange: <T extends keyof F>(filterName: T, newValue: F[T]) => void;
};

const FilteringContext = createContext<FilteringContextConfiguration>({
  filterValues: {},
  onSingleFilterChange: noop,
});

export function useFiltering<F extends FreitextSuche>(): FilteringContextConfiguration<F> {
  // we use any here to prevent error '{}' is assignable to the constraint of type 'F', but 'F' could be instantiated with a different subtype of constraint 'FreitextSuche'
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return useContext(FilteringContext) as any;
}

type FilteringProps<F, S extends string> = {
  className?: string;
  filterValues: F;
  onFilterValuesChange: (filterValues: F) => void;
} & Optional<SortingProps<S>>;

// Makes sure that either all required sorting props are present, or none of them (i.e. Filtering w/o sorting)
type SortingProps<S extends string> = {
  onSortChange: (sorting: Sorting<S>) => void;
  sorting: Sorting<S>;
  sortOptions: Option<S>[];
};

type Optional<T> = T | {
  [Property in keyof T]?: undefined
};

export function Filtering<F extends FreitextSuche, S extends string>({ children, filterValues, onFilterValuesChange, sortOptions, sorting, onSortChange, className }: PropsWithChildren<FilteringProps<F, S>>) {
  const formId = useId();
  const [expandArea, setExpandArea] = useState<ExpandArea>(null);
  const [currentFilterValues, setCurrentFilterValues] = useState(filterValues);

  // reflect changed values in internal state if they are set from the outside
  useEffect(() => setCurrentFilterValues(filterValues), [filterValues]);

  function handleSingleFilterChange<T extends keyof F>(filterName: T, value: F[T]) {
    const newFilterValues = {
      ...currentFilterValues,
      [filterName]: value,
    };

    setCurrentFilterValues(newFilterValues);
    onFilterValuesChange(newFilterValues);
  }

  const hasSorting = !isUndefined(sortOptions) && !isUndefined(sorting) && !isUndefined(onSortChange);
  const hasExpandableFilters = Children.count(children) > 0;

  const toggleExpandArea = (newArea: ExpandArea) => {
    setExpandArea(area => area === newArea ? null : newArea);
  };

  return (
    <form className={classNames(className, 'tds-filtering w-full p-200 md:p-300')} aria-labelledby={formId} onSubmit={event => event.preventDefault()}>
      <h2 id={formId} className="sr-only">Filtern nach...</h2>
      <div className="flex flex-col">
        <div className="flex items-center self-stretch gap-1">
          <Searchfield
            className="flex-grow "
            value={currentFilterValues['suchbegriff']}
            onValueChange={newValue => handleSingleFilterChange('suchbegriff', newValue)}
          />
          <div className="flex md:gap-4">
            {hasSorting ? <SortingController
              className="ml-300"
              onSortChange={onSortChange}
              sorting={sorting}
              sortOptions={sortOptions}
              expanded={expandArea === 'sort'}
              toggleExpandArea={() => {
                toggleExpandArea('sort');
              }}
            /> : null}
            {hasExpandableFilters ? <FilteringController toggleExpandArea={() => {
              toggleExpandArea('filter');
            }} expanded={expandArea === 'filter'} activeFilters={getActiveFilterCount(filterValues)} /> : null}
          </div>
        </div>

        {hasExpandableFilters && expandArea === 'filter' ?
          <div className="flex flex-col items-end self-stretch gap-200">
            <ExpandedFilters
              onSingleFilterChange={handleSingleFilterChange}
              values={currentFilterValues}
              onFilterReset={() => {
                onFilterValuesChange({} as F);
                setExpandArea(null);
              }}
            >
              {children}
            </ExpandedFilters>
          </div>
          : null
        }

        {hasSorting && expandArea === 'sort' ?
          <div className="flex flex-col items-center w-full pt-4">
            <SortingController
              isMobile
              className="ml-300"
              onSortChange={onSortChange}
              sorting={sorting}
              sortOptions={sortOptions}
              toggleExpandArea={() => {
                toggleExpandArea('sort');
              }}
            />
          </div>
          : null
        }
      </div>
    </form>
  );
}

function getActiveFilterCount<T extends FreitextSuche>(filters: T): number {
  const filtersWithoutFreetext = omit(filters, suchbegriff); // free text never affects active filter count

  if (isEmpty(filtersWithoutFreetext)) {
    return 0;
  }

  return Object
    .values(filtersWithoutFreetext)
    .filter(filterValue => filterValue !== undefined) // removes empty filters
    .filter(filterValue => !isEmpty(filterValue)) // removes filters with empty values
    .length;
}

type FilteringControllerProps = {
  expanded: boolean;
  activeFilters: number;
  toggleExpandArea: () => void;
};

export const FilteringController = ({ expanded, activeFilters, toggleExpandArea }: FilteringControllerProps) => (
  <div className="flex md:border-l border-tds-color-organizer-90 md:pl-50 md:gap-2">
    <ButtonActionQuietSurrogate icon={expanded ? CaretDoubleUp : CaretDoubleDown} onClick={toggleExpandArea}>
      <span className="hidden md:block" role="button">Filter</span>
    </ButtonActionQuietSurrogate>
    <Badge amount={activeFilters} srText="aktive Filter"/>
  </div>
);

type ExpandedFiltersProps<F> = {
  onSingleFilterChange: <T extends keyof F>(filterName: T, value: F[T]) => void;
  values: F;
  onFilterReset: () => void;
};

function ExpandedFilters<F extends FreitextSuche>({ children, values, onSingleFilterChange, onFilterReset }: PropsWithChildren<ExpandedFiltersProps<F>>) {
  return (
    <>
      <hr className="self-stretch border-tds-color-organizer-90 mt-200" />
      <FilteringContext.Provider value={{
        filterValues: values, // TODO 1989 Hier den internen State/Funktion rausgeben?
        onSingleFilterChange: onSingleFilterChange, // TODO 1989 Hier den internen State/Funktion rausgeben?
      }}>
        <div className="self-stretch grid grid-cols-4 gap-300">
          {children}
        </div>
      </FilteringContext.Provider>
      <ButtonCtaGhost icon={X} onClick={onFilterReset} type="button">Filter zurücksetzen</ButtonCtaGhost>
    </>
  );
}
